import { CollapseButton } from "components/CollapseButton/CollapseButton";
import { ResizablePagePanel } from "components/Layout/ResizablePanel/ResizablePagePanel";
import { CreateNew } from "components/Layout/Sidebar/components/CreateNew/CreateNew";
import ProjectTree from "containers/Projects/components/ProjectTree";
import { useEffect, useState } from "react";
import { useIsNarrow } from "utils/useMediaQuery";
import "./Sidebar.scss";

const minSidebarWidth = 195;
const maxSidebarWidth = 700;

export const Sidebar = () => {
    const isNarrow = useIsNarrow();
    const [sidebarWidth, setSidebarWidth] = useState(350);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Default the drawer collapsed on narrow (mobile) viewports so it doesn't
    // cover the page content, and expanded on wide viewports — re-applied whenever
    // the viewport crosses the `mobile` breakpoint. The user can still toggle it
    // manually within a breakpoint via the collapse button.
    useEffect(() => {
        setSidebarCollapsed(isNarrow);
    }, [isNarrow]);

    // On mobile the expanded drawer floats over the content as an overlay (see
    // Sidebar.scss) instead of pushing the page off-screen.
    const isOverlay = isNarrow && !sidebarCollapsed;

    const sidebar = (
        <aside
            className={`sidebar ${sidebarCollapsed ? "collapsed" : ""} ${
                isOverlay ? "sidebar--overlay" : ""
            }`}
            style={{
                width: sidebarCollapsed ? "1px" : isOverlay ? undefined : `${sidebarWidth}px`,
            }}
        >
            <div className="sidebar__content">
                <div className="sidebar__top">
                    <CreateNew />
                </div>
                <div className="sidebar__bottom">
                    <ProjectTree />
                </div>
                <div className="legalLinks">
                    <a
                        href="https://cloudomation.com/terms-of-use/cloudomation-platform-general-terms-and-conditions/"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Terms
                    </a>
                    <a
                        href="https://cloudomation.com/privacy-policy/"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Privacy
                    </a>
                    <a href="https://cloudomation.com/imprint/" target="_blank" rel="noreferrer">
                        Imprint
                    </a>
                </div>
            </div>

            <CollapseButton
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                isCollapsed={sidebarCollapsed}
                textShownOnHover="navigation"
            />
        </aside>
    );

    // On mobile, never wrap in the resizable panel: collapsed renders the bare
    // 1px rail (with its toggle) and expanded renders the fixed overlay + a
    // click-to-close backdrop.
    if (isNarrow) {
        return (
            <>
                {isOverlay && (
                    <div
                        className="sidebar__backdrop"
                        onClick={() => setSidebarCollapsed(true)}
                    />
                )}
                {sidebar}
            </>
        );
    }

    return sidebarCollapsed ? (
        sidebar
    ) : (
        <ResizablePagePanel
            width={sidebarWidth}
            minWidth={minSidebarWidth}
            maxWidth={maxSidebarWidth}
            setWidth={setSidebarWidth}
            enableHandlers={{
                right: true,
            }}
        >
            {sidebar}
        </ResizablePagePanel>
    );
};
